import React, { useCallback, useEffect, useMemo, useState } from 'react';

// ============================================================
// Onboarding kanban
// ============================================================
// Internal pipeline for delivering committed clients (mapping →
// enrichment → contacts → sign-up → invoicing). Separate from the CRM
// prospects board. Mirrors the CRM kanban patterns (drag-to-move,
// editable stages) and adds per-card LinkedIn/Sales Nav searches with
// automatic session-identifier stripping.

const TONE_CLASSES: Record<string, string> = {
  blue: 'bg-blue-50 border-blue-200',
  purple: 'bg-purple-50 border-purple-200',
  amber: 'bg-amber-50 border-amber-200',
  green: 'bg-green-50 border-green-200',
  red: 'bg-red-50 border-red-200',
  gray: 'bg-gray-100 border-gray-300',
  pink: 'bg-pink-50 border-pink-200',
  indigo: 'bg-indigo-50 border-indigo-200',
  teal: 'bg-teal-50 border-teal-200'
};
const TONE_NAMES = Object.keys(TONE_CLASSES);

interface Stage {
  id: number;
  key: string;
  label: string;
  tone: string;
  description: string | null;
  display_order: number;
  is_done: boolean;
  is_lost: boolean;
}

interface LinkedInSearch {
  id: number;
  label: string;
  url: string;
  created_at: string;
}

interface MappingRequest {
  id: number;
  onboarding_id: number;
  project_name: string | null;
  candidate_titles_exact: string | null;
  candidate_titles_contains: string | null;
  candidate_boolean: string | null;
  hm_titles_exact: string | null;
  hm_titles_contains: string | null;
  hm_boolean: string | null;
  candidate_location: string | null;
  candidate_radius_miles: number | null;
  company_hq_location: string | null;
  company_hq_radius_miles: number | null;
  results_count: number | null;
  status: 'pending' | 'mapped' | 'skipped';
  sort_order: number;
  created_at: string;
  updated_at: string;
}

interface Card {
  id: number;
  company: string | null;
  client_name: string | null;
  email: string | null;
  notes: string | null;
  stage: string;
  priority: number;
  linkedin_searches: LinkedInSearch[];
  prospect_id: number | null;
  prospect_name: string | null;
  completed_at: string | null;
  created_at: string;
  updated_at: string;
  mapping_request_count?: number;
  intake_token: string | null;
  intake_submitted_at: string | null;
  brief_approved_at: string | null;
  brief_approved_name: string | null;
  // Quote / scope (migration 114)
  candidate_search_url: string | null;
  company_search_url: string | null;
  candidate_result_count: number | null;
  company_result_count: number | null;
  estimated_cost_pence: number | null;
  quote_sent_at: string | null;
  quote_approved_at: string | null;
  quote_approved_name: string | null;
}

const authHeaders = (): HeadersInit => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json'
});

const Onboarding: React.FC = () => {
  const [stages, setStages] = useState<Stage[]>([]);
  const [cards, setCards] = useState<Card[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [dragOverStage, setDragOverStage] = useState<string | null>(null);

  const [showAdd, setShowAdd] = useState(false);
  const [editing, setEditing] = useState<Card | null>(null);
  const [showStagesModal, setShowStagesModal] = useState(false);

  const loadBoard = useCallback(async () => {
    try {
      setLoading(true);
      const [boardRes, stagesRes] = await Promise.all([
        fetch('/api/onboarding/board', { headers: authHeaders() }),
        fetch('/api/onboarding/stages', { headers: authHeaders() })
      ]);
      if (!boardRes.ok || !stagesRes.ok) throw new Error('Failed to load onboarding board');
      const boardData = await boardRes.json();
      const stagesData = await stagesRes.json();
      setCards(boardData.cards || []);
      setStages(stagesData.stages || []);
      setError(null);
    } catch (e: any) {
      setError(e.message || 'Failed to load board');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadBoard(); }, [loadBoard]);

  const byStage = useMemo(() => {
    const grouped: Record<string, Card[]> = {};
    for (const s of stages) grouped[s.key] = [];
    for (const c of cards) {
      if (!grouped[c.stage]) grouped[c.stage] = [];
      grouped[c.stage].push(c);
    }
    for (const key of Object.keys(grouped)) {
      grouped[key].sort((a, b) => {
        if ((b.priority || 0) !== (a.priority || 0)) return (b.priority || 0) - (a.priority || 0);
        return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
      });
    }
    return grouped;
  }, [cards, stages]);

  // Drag-and-drop
  const onDragStart = (e: React.DragEvent, id: number) => {
    setDraggingId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(id));
  };
  const onDragOver = (e: React.DragEvent, stageKey: string) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverStage !== stageKey) setDragOverStage(stageKey);
  };
  const onDrop = async (e: React.DragEvent, stageKey: string) => {
    e.preventDefault();
    setDragOverStage(null);
    const id = parseInt(e.dataTransfer.getData('text/plain'), 10);
    setDraggingId(null);
    if (!id) return;
    const card = cards.find(c => c.id === id);
    if (!card || card.stage === stageKey) return;

    setCards(prev => prev.map(c => c.id === id ? { ...c, stage: stageKey } : c));
    try {
      const res = await fetch(`/api/onboarding/cards/${id}`, {
        method: 'PATCH', headers: authHeaders(),
        body: JSON.stringify({ stage: stageKey })
      });
      if (!res.ok) throw new Error('Stage update failed');
      const data = await res.json();
      setCards(prev => prev.map(c => c.id === id ? data.card : c));
    } catch (err: any) {
      setError(err.message);
      loadBoard();
    }
  };

  return (
    <div className="p-6 max-w-[1600px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Onboarding</h1>
          <p className="text-sm text-gray-500">Delivery pipeline for committed clients — drag cards between stages.</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setShowStagesModal(true)} className="px-3 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">
            Manage stages
          </button>
          <button onClick={() => setShowAdd(true)} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium">
            + New client
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-600 underline text-xs">dismiss</button>
        </div>
      )}

      {loading ? (
        <div className="text-center py-12 text-gray-500 text-sm">Loading…</div>
      ) : (
        <div className="grid gap-3 overflow-x-auto" style={{ gridTemplateColumns: `repeat(${Math.max(stages.length, 1)}, minmax(220px, 1fr))` }}>
          {stages.map(stage => {
            const stageCards = byStage[stage.key] || [];
            const isDragOver = dragOverStage === stage.key;
            return (
              <div
                key={stage.key}
                onDragOver={e => onDragOver(e, stage.key)}
                onDragLeave={() => setDragOverStage(null)}
                onDrop={e => onDrop(e, stage.key)}
                className={`rounded-lg border-2 transition-colors ${TONE_CLASSES[stage.tone] || TONE_CLASSES.gray} ${isDragOver ? 'ring-2 ring-blue-400 ring-offset-1' : ''}`}
              >
                <div className="px-3 py-2 border-b border-current/10 sticky top-0 backdrop-blur-sm">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-gray-900 text-sm">{stage.label}</h3>
                    <span className="text-xs text-gray-500 font-medium">{stageCards.length}</span>
                  </div>
                  {stage.description && <p className="text-[10px] text-gray-500 mt-0.5">{stage.description}</p>}
                </div>
                <div className="p-2 space-y-2 min-h-[300px]">
                  {stageCards.map(card => (
                    <OnboardingCard
                      key={card.id}
                      card={card}
                      isDragging={draggingId === card.id}
                      onDragStart={onDragStart}
                      onClick={() => setEditing(card)}
                    />
                  ))}
                  {stageCards.length === 0 && (
                    <div className="text-xs text-gray-400 text-center py-6 italic">
                      {isDragOver ? 'Drop here' : 'No clients'}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showAdd && (
        <CardModal stages={stages} onClose={() => setShowAdd(false)} onSaved={() => { setShowAdd(false); loadBoard(); }} />
      )}
      {editing && (
        <CardModal
          card={editing}
          stages={stages}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); loadBoard(); }}
          onDeleted={() => { setEditing(null); loadBoard(); }}
        />
      )}
      {showStagesModal && (
        <StagesModal stages={stages} onClose={() => setShowStagesModal(false)} onChanged={loadBoard} />
      )}
    </div>
  );
};

// ============================================
// Card
// ============================================
const OnboardingCard: React.FC<{
  card: Card;
  isDragging: boolean;
  onDragStart: (e: React.DragEvent, id: number) => void;
  onClick: () => void;
}> = ({ card, isDragging, onDragStart, onClick }) => {
  const headline = card.company || card.client_name || 'Untitled';
  const subline = card.company ? card.client_name : null;
  const searchCount = card.linkedin_searches?.length || 0;
  const mappingCount = card.mapping_request_count || 0;
  const intakeSubmitted = !!card.intake_submitted_at;
  const briefApproved = !!card.brief_approved_at;
  const quoteSent = !!card.quote_sent_at;
  const quoteApproved = !!card.quote_approved_at;

  return (
    <div
      draggable
      onDragStart={e => onDragStart(e, card.id)}
      onClick={onClick}
      className={`bg-white rounded-md border border-gray-200 shadow-sm p-3 text-sm cursor-pointer hover:shadow-md transition-shadow ${isDragging ? 'opacity-50' : ''}`}
    >
      <div className="font-semibold text-gray-900 truncate">{headline}</div>
      {subline && <div className="text-xs text-gray-700 truncate mt-0.5">{subline}</div>}
      {card.notes && <div className="text-xs text-gray-500 mt-1 line-clamp-2">{card.notes}</div>}
      <div className="flex items-center gap-2 mt-2 flex-wrap">
        {mappingCount > 0 && (
          <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${
            briefApproved
              ? 'text-green-900 bg-green-200 border border-green-300'
              : intakeSubmitted
                ? 'text-green-800 bg-green-100 border border-green-200'
                : 'text-gray-700 bg-gray-100'
          }`} title={briefApproved ? `Brief approved by ${card.brief_approved_name || 'client'}` : intakeSubmitted ? 'Brief submitted, awaiting approval' : 'Brief in progress'}>
            📋 {mappingCount} mapping{mappingCount === 1 ? '' : 's'}{briefApproved ? ' ✓✓' : intakeSubmitted ? ' ✓' : ''}
          </span>
        )}
        {(quoteSent || quoteApproved) && (
          <span className={`inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded ${
            quoteApproved
              ? 'text-green-900 bg-green-200 border border-green-300'
              : 'text-amber-800 bg-amber-100 border border-amber-200'
          }`} title={quoteApproved ? `Quote approved by ${card.quote_approved_name || 'client'}` : 'Quote sent, awaiting approval'}>
            💰 {quoteApproved ? 'quote ✓' : 'quote sent'}
          </span>
        )}
        {searchCount > 0 && (
          <span className="inline-flex items-center gap-1 text-[10px] text-[#0077B5] bg-blue-50 px-1.5 py-0.5 rounded">
            🔗 {searchCount} search{searchCount === 1 ? '' : 'es'}
          </span>
        )}
        {card.priority > 0 && (
          <span className="text-[10px] text-amber-700 bg-amber-50 px-1.5 py-0.5 rounded">★ priority</span>
        )}
      </div>
    </div>
  );
};

// ============================================
// Mapping requirements section (rendered inside CardModal when editing).
// One row = one "search" the client wants us to run, matching the
// columns in the Mapping Project Brief spreadsheet:
//   Project Name | Cand Exact | Cand Contains | Cand Boolean |
//   Results Count | HM Exact | HM Contains | HM Boolean
// Adds/edits/deletes hit /api/onboarding/mapping-requests directly so we
// don't have to round-trip the whole card on every keystroke.
// ============================================

const STATUS_STYLES: Record<MappingRequest['status'], string> = {
  pending: 'bg-amber-100 text-amber-800 border-amber-300',
  mapped:  'bg-green-100 text-green-800 border-green-300',
  skipped: 'bg-gray-100 text-gray-700 border-gray-300'
};

const MappingRequirementsSection: React.FC<{
  cardId: number;
  intakeToken: string | null;
  intakeSubmittedAt: string | null;
  briefApprovedAt: string | null;
  briefApprovedName: string | null;
  onTokenChange: (token: string) => void;
  onApprovalReset: () => void;
}> = ({ cardId, intakeToken, intakeSubmittedAt, briefApprovedAt, briefApprovedName, onTokenChange, onApprovalReset }) => {
  const [rows, setRows] = useState<MappingRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);
  const [mintingLink, setMintingLink] = useState(false);

  const intakeUrl = intakeToken ? `${window.location.origin}/onboarding/intake/${intakeToken}` : null;

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const res = await fetch(`/api/onboarding/cards/${cardId}/mapping-requests`, { headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to load mapping requests');
      const data = await res.json();
      setRows(data.requests || []);
      setErr(null);
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }, [cardId]);

  useEffect(() => { load(); }, [load]);

  // Mint the link lazily on first click — saves us creating a token for
  // every card that never gets shared. Rotate forces a fresh one.
  const mintOrCopy = async (rotate: boolean) => {
    setMintingLink(true);
    try {
      const endpoint = rotate
        ? `/api/onboarding/cards/${cardId}/intake-link/rotate`
        : `/api/onboarding/cards/${cardId}/intake-link`;
      const res = await fetch(endpoint, { method: 'POST', headers: authHeaders() });
      if (!res.ok) throw new Error('Failed to generate link');
      const data = await res.json();
      onTokenChange(data.token);
      const url = `${window.location.origin}/onboarding/intake/${data.token}`;
      try {
        await navigator.clipboard.writeText(url);
        setLinkCopied(true);
        setTimeout(() => setLinkCopied(false), 2000);
      } catch {
        // Clipboard API can fail (no HTTPS, denied permission). User still sees the URL below.
      }
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setMintingLink(false);
    }
  };

  const copyExisting = async () => {
    if (!intakeUrl) return;
    try {
      await navigator.clipboard.writeText(intakeUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch { /* see above */ }
  };

  const resetApproval = async () => {
    if (!window.confirm('Clear the brief approval and ask the client to re-approve?')) return;
    await fetch(`/api/onboarding/cards/${cardId}/reset-brief-approval`, { method: 'POST', headers: authHeaders() });
    onApprovalReset();
  };

  const addRow = async () => {
    const res = await fetch(`/api/onboarding/cards/${cardId}/mapping-requests`, {
      method: 'POST', headers: authHeaders(), body: JSON.stringify({})
    });
    if (res.ok) {
      const data = await res.json();
      setRows(prev => [...prev, data.request]);
    }
  };

  // Field updates are pushed straight to the server (no local save button).
  // We optimistically update local state, then PATCH; on failure we reload.
  const updateField = async (id: number, patch: Partial<MappingRequest>) => {
    setRows(prev => prev.map(r => r.id === id ? { ...r, ...patch } : r));
    try {
      const res = await fetch(`/api/onboarding/mapping-requests/${id}`, {
        method: 'PATCH', headers: authHeaders(), body: JSON.stringify(patch)
      });
      if (!res.ok) throw new Error('save failed');
    } catch {
      load();
    }
  };

  const deleteRow = async (id: number) => {
    if (!window.confirm('Delete this mapping request row?')) return;
    setRows(prev => prev.filter(r => r.id !== id));
    await fetch(`/api/onboarding/mapping-requests/${id}`, { method: 'DELETE', headers: authHeaders() });
  };

  const cycleStatus = (id: number, current: MappingRequest['status']) => {
    const next = current === 'pending' ? 'mapped' : current === 'mapped' ? 'skipped' : 'pending';
    updateField(id, { status: next });
  };

  return (
    <div className="border-t border-gray-100 pt-4">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-semibold text-gray-900">Mapping requirements</h3>
        <button
          onClick={addRow}
          className="text-xs px-2.5 py-1 rounded bg-gray-900 text-white hover:bg-gray-700"
        >
          + Add row
        </button>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        One row per search to map — matches the Mapping Project Brief spreadsheet. Click the status chip to cycle pending → mapped → skipped.
      </p>

      {/* Approval state — surfaces above the link controls so it's the
          first thing you notice when opening a card that's awaiting/has
          received sign-off. */}
      {briefApprovedAt ? (
        <div className="bg-green-50 border border-green-300 rounded-lg p-3 mb-3 flex items-center justify-between gap-2">
          <div className="text-xs text-green-900">
            <span className="font-semibold">✓ Brief approved</span>
            {briefApprovedName && <> by <strong>{briefApprovedName}</strong></>}
            {' '}on {new Date(briefApprovedAt).toLocaleDateString()}
          </div>
          <button
            onClick={resetApproval}
            className="text-[11px] px-2 py-1 rounded border border-green-300 bg-white text-green-800 hover:bg-green-100"
            title="Clear approval if you've made changes the client should re-confirm"
          >
            Reset approval
          </button>
        </div>
      ) : intakeSubmittedAt ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 text-xs text-amber-900">
          📝 Brief submitted on {new Date(intakeSubmittedAt).toLocaleDateString()} — awaiting client approval.
        </div>
      ) : null}

      {/* Share-with-client link */}
      <div className="bg-blue-50/60 border border-blue-200 rounded-lg p-3 mb-3">
        <div className="flex items-center justify-between mb-1">
          <div className="text-xs font-semibold text-blue-900">Share with client</div>
          {intakeSubmittedAt && (
            <div className="text-[10px] text-green-700 bg-green-100 border border-green-200 px-2 py-0.5 rounded">
              Submitted {new Date(intakeSubmittedAt).toLocaleDateString()}
            </div>
          )}
        </div>
        <p className="text-[11px] text-blue-800/80 mb-2">
          Send the client a link so they can fill in the brief themselves. Resubmissions overwrite the rows above.
        </p>
        {intakeUrl ? (
          <div className="flex items-center gap-2">
            <input readOnly value={intakeUrl} className="flex-1 px-2 py-1.5 border border-blue-200 rounded text-xs font-mono bg-white" onFocus={e => e.currentTarget.select()} />
            <button onClick={copyExisting} className="text-xs px-2.5 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 whitespace-nowrap">
              {linkCopied ? 'Copied!' : 'Copy'}
            </button>
            <button onClick={() => mintOrCopy(true)} disabled={mintingLink} className="text-xs px-2.5 py-1.5 rounded bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 whitespace-nowrap disabled:opacity-50" title="Generate a new link (revokes the current one)">
              Rotate
            </button>
          </div>
        ) : (
          <button onClick={() => mintOrCopy(false)} disabled={mintingLink} className="text-xs px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
            {mintingLink ? 'Generating…' : 'Generate intake link'}
          </button>
        )}
      </div>

      {err && <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-800 mb-2">{err}</div>}
      {loading && <div className="text-xs text-gray-500">Loading…</div>}

      {!loading && rows.length === 0 && (
        <div className="text-xs text-gray-500 bg-gray-50 border border-dashed border-gray-300 rounded p-3 text-center">
          No mapping requirements yet. Add a row to capture the brief.
        </div>
      )}

      {!loading && rows.length > 0 && (
        <div className="space-y-2">
          {rows.map((r, idx) => (
            <div key={r.id} className="border border-gray-200 rounded-lg p-3 space-y-2 bg-white">
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-gray-500 w-6">#{idx + 1}</span>
                <input
                  value={r.project_name || ''}
                  onChange={e => updateField(r.id, { project_name: e.target.value })}
                  placeholder="Project name (e.g. Nordics SAP Consultants)"
                  className="flex-1 px-2 py-1 border border-gray-300 rounded text-sm"
                />
                <button
                  onClick={() => cycleStatus(r.id, r.status)}
                  className={`text-[10px] uppercase tracking-wider px-2 py-1 border rounded ${STATUS_STYLES[r.status]}`}
                  title="Click to change status"
                >
                  {r.status}
                </button>
                <button onClick={() => deleteRow(r.id)} className="text-gray-400 hover:text-red-600 text-sm" title="Delete row">✕</button>
              </div>

              <div className="grid grid-cols-2 gap-3">
                {/* Candidate titles */}
                <div className="bg-green-50/40 border border-green-200 rounded p-2 space-y-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-green-700 font-semibold">👤 Candidate titles</div>
                  <input
                    value={r.candidate_titles_exact || ''}
                    onChange={e => updateField(r.id, { candidate_titles_exact: e.target.value })}
                    placeholder={'Exact match (e.g. "SAP FICO Consultant", "SAP MM Consultant") — quotes added automatically'}
                    className="w-full px-2 py-1 border border-gray-200 rounded text-xs"
                  />
                  <input
                    value={r.candidate_titles_contains || ''}
                    onChange={e => updateField(r.id, { candidate_titles_contains: e.target.value })}
                    placeholder="Contains match (e.g. SAP Consultant, ERP Consultant)"
                    className="w-full px-2 py-1 border border-gray-200 rounded text-xs"
                  />
                  <input
                    value={r.candidate_boolean || ''}
                    onChange={e => updateField(r.id, { candidate_boolean: e.target.value })}
                    placeholder='Optional Boolean (e.g. "S/4HANA" NOT Junior)'
                    className="w-full px-2 py-1 border border-gray-200 rounded text-xs font-mono"
                  />
                </div>

                {/* Hiring manager titles */}
                <div className="bg-purple-50/40 border border-purple-200 rounded p-2 space-y-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-purple-700 font-semibold">👔 Hiring manager titles</div>
                  <input
                    value={r.hm_titles_exact || ''}
                    onChange={e => updateField(r.id, { hm_titles_exact: e.target.value })}
                    placeholder={'Exact match (e.g. "Head of SAP") — quotes added automatically'}
                    className="w-full px-2 py-1 border border-gray-200 rounded text-xs"
                  />
                  <input
                    value={r.hm_titles_contains || ''}
                    onChange={e => updateField(r.id, { hm_titles_contains: e.target.value })}
                    placeholder="Contains match (e.g. IT Director, ERP Manager)"
                    className="w-full px-2 py-1 border border-gray-200 rounded text-xs"
                  />
                  <input
                    value={r.hm_boolean || ''}
                    onChange={e => updateField(r.id, { hm_boolean: e.target.value })}
                    placeholder="Optional Boolean"
                    className="w-full px-2 py-1 border border-gray-200 rounded text-xs font-mono"
                  />
                </div>
              </div>

              {/* Location filter — mirrors the public intake form */}
              <div className="grid grid-cols-2 gap-3">
                <div className="bg-green-50/40 border border-green-200 rounded p-2 space-y-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-green-700 font-semibold">📍 Candidate location</div>
                  <input
                    value={r.candidate_location || ''}
                    onChange={e => updateField(r.id, { candidate_location: e.target.value })}
                    placeholder='e.g. United Kingdom, London, or postcode'
                    className="w-full px-2 py-1 border border-gray-200 rounded text-xs"
                  />
                  <div className="flex items-center gap-1.5">
                    <label className="text-[10px] text-gray-500 whitespace-nowrap">Radius</label>
                    <input
                      type="number"
                      value={r.candidate_radius_miles ?? ''}
                      onChange={e => updateField(r.id, { candidate_radius_miles: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
                      placeholder="opt."
                      className="w-16 px-2 py-1 border border-gray-200 rounded text-xs"
                    />
                    <span className="text-[10px] text-gray-500">miles</span>
                  </div>
                </div>
                <div className="bg-blue-50/40 border border-blue-200 rounded p-2 space-y-1.5">
                  <div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">📍 Company HQ location</div>
                  <input
                    value={r.company_hq_location || ''}
                    onChange={e => updateField(r.id, { company_hq_location: e.target.value })}
                    placeholder='e.g. United Kingdom, London, or postcode'
                    className="w-full px-2 py-1 border border-gray-200 rounded text-xs"
                  />
                  <div className="flex items-center gap-1.5">
                    <label className="text-[10px] text-gray-500 whitespace-nowrap">Radius</label>
                    <input
                      type="number"
                      value={r.company_hq_radius_miles ?? ''}
                      onChange={e => updateField(r.id, { company_hq_radius_miles: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
                      placeholder="opt."
                      className="w-16 px-2 py-1 border border-gray-200 rounded text-xs"
                    />
                    <span className="text-[10px] text-gray-500">miles</span>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-2">
                <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold">Expected results</label>
                <input
                  type="number"
                  value={r.results_count ?? ''}
                  onChange={e => updateField(r.id, { results_count: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
                  placeholder="e.g. 250"
                  className="w-24 px-2 py-1 border border-gray-200 rounded text-xs"
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// ============================================
// Internal quote-prep section.
// After the client has approved the brief, the team runs the Sales Nav
// search and records: candidate URL, company URL, the two result counts,
// and the estimated cost. Send Quote stamps quote_sent_at; the client's
// next visit to /onboarding/intake/:token shows the public Quote view
// (counts + cost — never the URLs).
// ============================================

const formatGBP = (pence: number | null): string =>
  pence == null ? '' : (pence / 100).toFixed(2);

const parseGBPInputToPence = (raw: string): number | null => {
  const t = raw.trim();
  if (!t) return null;
  const n = Number(t.replace(/[£,\s]/g, ''));
  if (!Number.isFinite(n)) return null;
  return Math.round(n * 100);
};

const QuotePrepSection: React.FC<{
  card: Card;
  intakeToken: string | null;
  onCardUpdated: (patch: Partial<Card>) => void;
}> = ({ card, intakeToken, onCardUpdated }) => {
  const [candUrl, setCandUrl]   = useState(card.candidate_search_url || '');
  const [compUrl, setCompUrl]   = useState(card.company_search_url || '');
  const [candCount, setCandCount] = useState<string>(card.candidate_result_count != null ? String(card.candidate_result_count) : '');
  const [compCount, setCompCount] = useState<string>(card.company_result_count   != null ? String(card.company_result_count)   : '');
  const [costInput, setCostInput] = useState<string>(formatGBP(card.estimated_cost_pence));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [linkCopied, setLinkCopied] = useState(false);

  // Push the new values to the server, mirroring the same PATCH the rest
  // of the modal uses. Quote URLs go through the same sanitiser as the
  // legacy linkedin_searches (server-side).
  const saveAll = async () => {
    setSaving(true);
    setErr(null);
    try {
      const body = {
        candidateSearchUrl: candUrl.trim() || null,
        companySearchUrl: compUrl.trim() || null,
        candidateResultCount: candCount.trim() === '' ? null : parseInt(candCount, 10),
        companyResultCount: compCount.trim() === '' ? null : parseInt(compCount, 10),
        estimatedCostPence: parseGBPInputToPence(costInput)
      };
      const res = await fetch(`/api/onboarding/cards/${card.id}`, {
        method: 'PATCH', headers: authHeaders(), body: JSON.stringify(body)
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Save failed'); }
      const data = await res.json();
      // Echo server-side sanitised values back into local state so the URL
      // inputs show the cleaned form.
      if (data.card) {
        setCandUrl(data.card.candidate_search_url || '');
        setCompUrl(data.card.company_search_url || '');
        onCardUpdated({
          candidate_search_url: data.card.candidate_search_url,
          company_search_url: data.card.company_search_url,
          candidate_result_count: data.card.candidate_result_count,
          company_result_count: data.card.company_result_count,
          estimated_cost_pence: data.card.estimated_cost_pence
        });
      }
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const sendQuote = async () => {
    // Persist any unsaved edits first so the backend's "send-quote"
    // readiness check sees the latest numbers.
    await saveAll();
    setSaving(true);
    setErr(null);
    try {
      const res = await fetch(`/api/onboarding/cards/${card.id}/send-quote`, {
        method: 'POST', headers: authHeaders()
      });
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Send failed'); }
      const data = await res.json();
      onCardUpdated({ quote_sent_at: data.sent_at });
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const resetQuote = async () => {
    if (!window.confirm('Clear the quote and any approval? The client will see the brief view again instead of the quote.')) return;
    await fetch(`/api/onboarding/cards/${card.id}/reset-quote`, { method: 'POST', headers: authHeaders() });
    onCardUpdated({ quote_sent_at: null, quote_approved_at: null, quote_approved_name: null });
  };

  const intakeUrl = intakeToken ? `${window.location.origin}/onboarding/intake/${intakeToken}` : null;
  const copyQuoteLink = async () => {
    if (!intakeUrl) return;
    try {
      await navigator.clipboard.writeText(intakeUrl);
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2000);
    } catch { /* clipboard can fail on non-HTTPS */ }
  };

  return (
    <div className="border-t border-gray-100 pt-4">
      <div className="flex items-center justify-between mb-1">
        <h3 className="text-sm font-semibold text-gray-900">Quote prep</h3>
        <span className="text-[10px] uppercase tracking-wider text-gray-500">internal — URLs never shown to client</span>
      </div>
      <p className="text-xs text-gray-500 mb-3">
        Paste the Sales Nav URLs, set the candidate result count, and enter the estimated cost. Company count is optional — it's only known precisely after the candidate map runs. Click Send quote when ready.
      </p>

      {/* Quote status banner */}
      {card.quote_approved_at ? (
        <div className="bg-green-50 border border-green-300 rounded-lg p-3 mb-3 flex items-center justify-between gap-2">
          <div className="text-xs text-green-900">
            ✓ Quote approved {card.quote_approved_name && <>by <strong>{card.quote_approved_name}</strong></>} on {new Date(card.quote_approved_at).toLocaleDateString()}
          </div>
          <button onClick={resetQuote} className="text-[11px] px-2 py-1 rounded border border-green-300 bg-white text-green-800 hover:bg-green-100">
            Reset quote
          </button>
        </div>
      ) : card.quote_sent_at ? (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-3 flex items-center justify-between gap-2">
          <div className="text-xs text-amber-900">
            📤 Quote sent on {new Date(card.quote_sent_at).toLocaleDateString()} — awaiting client approval.
          </div>
          <div className="flex items-center gap-2">
            {intakeUrl && (
              <button onClick={copyQuoteLink} className="text-[11px] px-2 py-1 rounded bg-blue-600 text-white hover:bg-blue-700 whitespace-nowrap">
                {linkCopied ? 'Copied!' : 'Copy quote link'}
              </button>
            )}
            <button onClick={resetQuote} className="text-[11px] px-2 py-1 rounded border border-amber-300 bg-white text-amber-800 hover:bg-amber-100">
              Reset
            </button>
          </div>
        </div>
      ) : null}

      {err && <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-800 mb-2">{err}</div>}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
        <div className="bg-green-50/40 border border-green-200 rounded p-2 space-y-1.5">
          <div className="text-[10px] uppercase tracking-wider text-green-700 font-semibold">👤 Candidate search</div>
          <textarea value={candUrl} onChange={e => setCandUrl(e.target.value)} rows={2} placeholder="Sales Nav URL (sanitised on save)" className="w-full px-2 py-1 border border-gray-200 rounded text-xs font-mono resize-y" />
          <div className="flex items-center gap-2">
            <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold whitespace-nowrap">Result count</label>
            <input type="number" value={candCount} onChange={e => setCandCount(e.target.value)} placeholder="e.g. 1200" className="flex-1 px-2 py-1 border border-gray-200 rounded text-xs" />
          </div>
        </div>
        <div className="bg-blue-50/40 border border-blue-200 rounded p-2 space-y-1.5">
          <div className="text-[10px] uppercase tracking-wider text-blue-700 font-semibold">🏢 Company search</div>
          <textarea value={compUrl} onChange={e => setCompUrl(e.target.value)} rows={2} placeholder="Sales Nav URL (sanitised on save)" className="w-full px-2 py-1 border border-gray-200 rounded text-xs font-mono resize-y" />
          <div className="flex items-center gap-2">
            <label className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold whitespace-nowrap">Result count <span className="text-gray-400 normal-case">(estimate)</span></label>
            <input type="number" value={compCount} onChange={e => setCompCount(e.target.value)} placeholder="optional" className="flex-1 px-2 py-1 border border-gray-200 rounded text-xs" />
          </div>
          {/* Suggest 35% of the candidate count — based on historical
              project data. One-click apply rather than auto-fill, since
              real ratios vary by market / company-filter size etc. */}
          {(() => {
            const candNum = parseInt(candCount, 10);
            if (!Number.isFinite(candNum) || candNum <= 0) return null;
            const suggestion = Math.round(candNum * 0.35);
            if (String(suggestion) === compCount) return null;
            return (
              <button
                type="button"
                onClick={() => setCompCount(String(suggestion))}
                className="text-[10px] text-blue-700 hover:underline self-start"
                title="Historical average: company count ≈ 35% of candidate count. Click to use this."
              >
                💡 Suggest {suggestion.toLocaleString()} (35% of candidate count)
              </button>
            );
          })()}
        </div>
      </div>

      <div className="flex items-center gap-3 mb-3">
        <label className="text-xs font-semibold text-gray-700">Estimated cost (£)</label>
        <input value={costInput} onChange={e => setCostInput(e.target.value)} placeholder="e.g. 1250.00" className="w-32 px-2 py-1 border border-gray-200 rounded text-sm" />
      </div>

      <div className="flex items-center gap-2">
        <button onClick={saveAll} disabled={saving} className="text-xs px-3 py-1.5 rounded bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-50">
          {saving ? 'Saving…' : 'Save'}
        </button>
        {!card.quote_sent_at && !card.quote_approved_at && (
          <button onClick={sendQuote} disabled={saving} className="text-xs px-3 py-1.5 rounded bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50">
            Send quote to client →
          </button>
        )}
      </div>
    </div>
  );
};

// ============================================
// Card add/edit modal (includes LinkedIn search manager)
// ============================================
const CardModal: React.FC<{
  card?: Card;
  stages: Stage[];
  onClose: () => void;
  onSaved: () => void;
  onDeleted?: () => void;
}> = ({ card, stages, onClose, onSaved, onDeleted }) => {
  const isEdit = !!card;
  const [company, setCompany] = useState(card?.company || '');
  const [clientName, setClientName] = useState(card?.client_name || '');
  const [email, setEmail] = useState(card?.email || '');
  const [notes, setNotes] = useState(card?.notes || '');
  const [stage, setStage] = useState(card?.stage || stages[0]?.key || 'qualification');
  const [priority, setPriority] = useState(card?.priority || 0);
  const [searches, setSearches] = useState<LinkedInSearch[]>(card?.linkedin_searches || []);
  // Mirror card.intake_token in local state so the "Share with client" UI
  // can update reactively after generate/rotate without refetching the board.
  const [intakeToken, setIntakeToken] = useState<string | null>(card?.intake_token || null);
  // Mirror approval state so the Reset action gives instant feedback
  // without waiting for the whole board to reload.
  const [briefApprovedAt, setBriefApprovedAt] = useState<string | null>(card?.brief_approved_at || null);
  const [briefApprovedName, setBriefApprovedName] = useState<string | null>(card?.brief_approved_name || null);
  // Quote-side mirrors so Send Quote / Reset update without a board reload.
  const [quoteSentAt, setQuoteSentAt]         = useState<string | null>(card?.quote_sent_at || null);
  const [quoteApprovedAt, setQuoteApprovedAt] = useState<string | null>(card?.quote_approved_at || null);
  const [quoteApprovedName, setQuoteApprovedName] = useState<string | null>(card?.quote_approved_name || null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // LinkedIn search adder
  const [newSearchLabel, setNewSearchLabel] = useState('');
  const [newSearchUrl, setNewSearchUrl] = useState('');
  const [sanitisedPreview, setSanitisedPreview] = useState<string | null>(null);
  const [addingSearch, setAddingSearch] = useState(false);
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const save = async () => {
    if (!company.trim() && !clientName.trim()) { setErr('A company or client name is required'); return; }
    setSaving(true);
    setErr(null);
    try {
      const body = JSON.stringify({ company, clientName, email, notes, stage, priority, linkedinSearches: searches });
      const res = await fetch(
        isEdit ? `/api/onboarding/cards/${card!.id}` : '/api/onboarding/cards',
        { method: isEdit ? 'PATCH' : 'POST', headers: authHeaders(), body }
      );
      if (!res.ok) { const d = await res.json().catch(() => ({})); throw new Error(d.error || 'Save failed'); }
      onSaved();
    } catch (e: any) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!card || !window.confirm('Delete this onboarding card? This cannot be undone.')) return;
    await fetch(`/api/onboarding/cards/${card.id}`, { method: 'DELETE', headers: authHeaders() });
    onDeleted?.();
  };

  // Live sanitise preview as the user pastes a URL.
  const previewSanitise = async (url: string) => {
    setNewSearchUrl(url);
    if (!url.trim()) { setSanitisedPreview(null); return; }
    try {
      const res = await fetch('/api/onboarding/sanitise-url', {
        method: 'POST', headers: authHeaders(), body: JSON.stringify({ url })
      });
      const data = await res.json();
      setSanitisedPreview(data.sanitised || null);
    } catch {
      setSanitisedPreview(null);
    }
  };

  const addSearch = () => {
    if (!sanitisedPreview) return;
    setAddingSearch(true);
    const entry: LinkedInSearch = {
      id: Date.now(),
      label: newSearchLabel.trim() || 'Sales Nav search',
      url: sanitisedPreview,
      created_at: new Date().toISOString()
    };
    setSearches(prev => [...prev, entry]);
    setNewSearchLabel('');
    setNewSearchUrl('');
    setSanitisedPreview(null);
    setAddingSearch(false);
  };

  const removeSearch = (id: number) => setSearches(prev => prev.filter(s => s.id !== id));

  const copyUrl = (s: LinkedInSearch) => {
    navigator.clipboard.writeText(s.url);
    setCopiedId(s.id);
    setTimeout(() => setCopiedId(null), 1500);
  };

  const [copiedPreview, setCopiedPreview] = useState(false);
  const copySanitised = async () => {
    if (!sanitisedPreview) return;
    try {
      await navigator.clipboard.writeText(sanitisedPreview);
      setCopiedPreview(true);
      setTimeout(() => setCopiedPreview(false), 1500);
    } catch { /* clipboard can fail on non-HTTPS */ }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">{isEdit ? 'Edit client' : 'New client'}</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>

        <div className="px-6 py-5 space-y-4">
          {err && <div className="bg-red-50 border border-red-200 rounded-lg p-2.5 text-sm text-red-800">{err}</div>}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Company</label>
              <input value={company} onChange={e => setCompany(e.target.value)} placeholder="Acme Recruitment" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Client name</label>
              <input value={clientName} onChange={e => setClientName(e.target.value)} placeholder="Jane Smith" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Email</label>
              <input value={email} onChange={e => setEmail(e.target.value)} placeholder="jane@acme.com" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            </div>
            <div>
              <label className="block text-xs font-semibold text-gray-700 mb-1">Stage</label>
              <select value={stage} onChange={e => setStage(e.target.value)} className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm bg-white">
                {stages.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1">Notes</label>
            <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4} placeholder="Requirements, what they asked for on the call, titles they want mapped…" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm resize-y" />
          </div>

          <label className="flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={priority > 0} onChange={e => setPriority(e.target.checked ? 1 : 0)} />
            Mark as priority (floats to the top of its column)
          </label>

          {/* Mapping requirements — only for existing cards (need an id) */}
          {isEdit && card && (
            <MappingRequirementsSection
              cardId={card.id}
              intakeToken={intakeToken}
              intakeSubmittedAt={card.intake_submitted_at}
              briefApprovedAt={briefApprovedAt}
              briefApprovedName={briefApprovedName}
              onTokenChange={setIntakeToken}
              onApprovalReset={() => { setBriefApprovedAt(null); setBriefApprovedName(null); }}
            />
          )}

          {/* Quote prep + send — internal step between brief approval and extraction */}
          {isEdit && card && (
            <QuotePrepSection
              card={{ ...card,
                quote_sent_at: quoteSentAt,
                quote_approved_at: quoteApprovedAt,
                quote_approved_name: quoteApprovedName }}
              intakeToken={intakeToken}
              onCardUpdated={(patch) => {
                if (patch.quote_sent_at !== undefined) setQuoteSentAt(patch.quote_sent_at);
                if (patch.quote_approved_at !== undefined) setQuoteApprovedAt(patch.quote_approved_at);
                if (patch.quote_approved_name !== undefined) setQuoteApprovedName(patch.quote_approved_name);
              }}
            />
          )}

          {/* LinkedIn searches */}
          <div className="border-t border-gray-100 pt-4">
            <h3 className="text-sm font-semibold text-gray-900 mb-1">LinkedIn / Sales Nav searches</h3>
            <p className="text-xs text-gray-500 mb-3">
              Paste a Sales Navigator URL — session identifiers are stripped automatically before saving, so it's safe to share.
            </p>

            {searches.length > 0 && (
              <div className="space-y-2 mb-3">
                {searches.map(s => (
                  <div key={s.id} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                    <span className="text-[#0077B5]">🔗</span>
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-medium text-gray-900 truncate">{s.label}</div>
                      <div className="text-[10px] text-gray-400 truncate">{s.url}</div>
                    </div>
                    <button onClick={() => copyUrl(s)} className="text-xs px-2 py-1 rounded bg-white border border-gray-300 hover:bg-gray-50 whitespace-nowrap">
                      {copiedId === s.id ? 'Copied!' : 'Copy'}
                    </button>
                    <a href={s.url} target="_blank" rel="noreferrer" className="text-xs px-2 py-1 rounded bg-[#0077B5] text-white hover:bg-[#006097] whitespace-nowrap">Open</a>
                    <button onClick={() => removeSearch(s.id)} className="text-gray-400 hover:text-red-600 text-sm">✕</button>
                  </div>
                ))}
              </div>
            )}

            <div className="space-y-2 bg-gray-50/60 border border-dashed border-gray-300 rounded-lg p-3">
              <input value={newSearchLabel} onChange={e => setNewSearchLabel(e.target.value)} placeholder="Label (e.g. Commissioning Managers — US)" className="w-full px-3 py-1.5 border border-gray-300 rounded text-sm" />
              <textarea value={newSearchUrl} onChange={e => previewSanitise(e.target.value)} rows={2} placeholder="Paste Sales Navigator URL here…" className="w-full px-3 py-1.5 border border-gray-300 rounded text-xs font-mono resize-y" />
              {sanitisedPreview && (
                <div className="text-[10px] text-green-700 bg-green-50 border border-green-200 rounded px-2 py-1.5 flex items-start gap-2">
                  <div className="flex-1 min-w-0">
                    <span className="font-semibold">Cleaned:</span> <span className="break-all">{sanitisedPreview}</span>
                  </div>
                  <button
                    onClick={copySanitised}
                    className="flex-shrink-0 text-[10px] px-2 py-0.5 rounded bg-white border border-green-300 text-green-700 hover:bg-green-100 whitespace-nowrap"
                  >
                    {copiedPreview ? '✓ Copied' : '📋 Copy'}
                  </button>
                </div>
              )}
              <button
                onClick={addSearch}
                disabled={!sanitisedPreview || addingSearch}
                className="text-sm px-3 py-1.5 bg-gray-900 text-white rounded hover:bg-gray-700 disabled:opacity-40"
              >
                Add search
              </button>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
          {isEdit ? (
            <button onClick={remove} className="text-sm text-red-600 hover:text-red-800">Delete</button>
          ) : <span />}
          <div className="flex items-center gap-2">
            <button onClick={onClose} className="px-4 py-2 text-sm text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
            <button onClick={save} disabled={saving} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// ============================================
// Stages manager modal
// ============================================
const StagesModal: React.FC<{ stages: Stage[]; onClose: () => void; onChanged: () => void }> = ({ stages, onClose, onChanged }) => {
  const [local, setLocal] = useState<Stage[]>(stages);
  const [newLabel, setNewLabel] = useState('');
  const [newTone, setNewTone] = useState('gray');
  const [busy, setBusy] = useState(false);

  const reload = async () => {
    const res = await fetch('/api/onboarding/stages', { headers: authHeaders() });
    const data = await res.json();
    setLocal(data.stages || []);
    onChanged();
  };

  const addStage = async () => {
    if (!newLabel.trim()) return;
    setBusy(true);
    await fetch('/api/onboarding/stages', {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ label: newLabel.trim(), tone: newTone })
    });
    setNewLabel('');
    setNewTone('gray');
    setBusy(false);
    reload();
  };

  const updateStage = async (id: number, patch: Partial<Stage>) => {
    await fetch(`/api/onboarding/stages/${id}`, { method: 'PATCH', headers: authHeaders(), body: JSON.stringify(patch) });
    reload();
  };

  const deleteStage = async (id: number) => {
    if (!window.confirm('Delete this stage? Cards in it move to the first remaining stage.')) return;
    await fetch(`/api/onboarding/stages/${id}`, { method: 'DELETE', headers: authHeaders() });
    reload();
  };

  const move = async (idx: number, dir: -1 | 1) => {
    const target = idx + dir;
    if (target < 0 || target >= local.length) return;
    const reordered = [...local];
    [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
    setLocal(reordered);
    await fetch('/api/onboarding/stages/reorder', {
      method: 'POST', headers: authHeaders(),
      body: JSON.stringify({ order: reordered.map((s, i) => ({ id: s.id, displayOrder: i + 1 })) })
    });
    onChanged();
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Manage stages</h2>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>
        <div className="px-6 py-4 space-y-2">
          {local.map((s, idx) => (
            <div key={s.id} className={`flex items-center gap-2 border-2 rounded-lg p-2.5 ${TONE_CLASSES[s.tone] || TONE_CLASSES.gray}`}>
              <div className="flex flex-col">
                <button onClick={() => move(idx, -1)} disabled={idx === 0} className="text-xs text-gray-500 disabled:opacity-30 leading-none">▲</button>
                <button onClick={() => move(idx, 1)} disabled={idx === local.length - 1} className="text-xs text-gray-500 disabled:opacity-30 leading-none">▼</button>
              </div>
              <input
                defaultValue={s.label}
                onBlur={e => e.target.value !== s.label && updateStage(s.id, { label: e.target.value } as Partial<Stage>)}
                className="flex-1 px-2 py-1 bg-white/70 border border-gray-300 rounded text-sm"
              />
              <select value={s.tone} onChange={e => updateStage(s.id, { tone: e.target.value } as Partial<Stage>)} className="px-2 py-1 bg-white border border-gray-300 rounded text-xs">
                {TONE_NAMES.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
              <button onClick={() => deleteStage(s.id)} className="text-gray-400 hover:text-red-600 text-sm">✕</button>
            </div>
          ))}
        </div>
        <div className="px-6 py-4 border-t border-gray-200">
          <div className="flex items-center gap-2">
            <input value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="New stage label" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
            <select value={newTone} onChange={e => setNewTone(e.target.value)} className="px-2 py-2 border border-gray-300 rounded-lg text-xs">
              {TONE_NAMES.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <button onClick={addStage} disabled={busy || !newLabel.trim()} className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">Add</button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Onboarding;
